import { useTourStore } from '@/stores/use-tour-store'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

import mestreTigerPointing from '@/assets/companions/mestre-tiger-pointing.png'
import mestreTigerThumbsUp from '@/assets/companions/mestre-tiger-thumbs-up.png'
import mestreTigerLaptop from '@/assets/companions/mestre-tiger-laptop.png'
import mestreBullPointing from '@/assets/companions/mestre-bull-pointing.png'
import mestreBullThumbsUp from '@/assets/companions/mestre-bull-thumbs-up.png'

type Character = 'tiger' | 'bull'
type Pose = 'pointing' | 'thumbs-up' | 'laptop'
type CompanionPosition = 'bottom-right' | 'near-sidebar' | 'center' | 'near-content'

const CHARACTER_IMAGES: Record<Character, Record<Pose, string>> = {
  tiger: {
    pointing: mestreTigerPointing,
    'thumbs-up': mestreTigerThumbsUp,
    laptop: mestreTigerLaptop,
  },
  bull: {
    pointing: mestreBullPointing,
    'thumbs-up': mestreBullThumbsUp,
    laptop: mestreBullPointing, // fallback
  },
}

const CHARACTER_NAMES: Record<Character, string> = {
  tiger: 'Mestre Tiger',
  bull: 'Mestre Bull',
}

interface StepConfig {
  character: Character
  pose: Pose
  message: string
  showNext: boolean
  showClose: boolean
  showFinancialChoice?: boolean
  isLast?: boolean
  position: CompanionPosition
}

const STEP_CONFIG: Record<string, StepConfig> = {
  'create-admin': {
    character: 'tiger',
    pose: 'pointing',
    message:
      'Bem-vindo! Vamos criar seu usuário administrador. Clique no botão **Novo Usuário** destacado acima para começar.',
    showNext: false,
    showClose: false,
    position: 'near-content',
  },
  'ask-financial': {
    character: 'bull',
    pose: 'pointing',
    message:
      'Deseja controlar o financeiro pela plataforma? Cobranças, pagamentos e inadimplência.',
    showNext: false,
    showClose: true,
    showFinancialChoice: true,
    position: 'center',
  },
  'show-financial': {
    character: 'tiger',
    pose: 'laptop',
    message:
      'Na aba **Financeiro** você configura a integração de cobranças com o Asaas. Aqui você gerencia pagamentos, boletos e PIX.',
    showNext: true,
    showClose: true,
    position: 'near-sidebar',
  },
  'show-students': {
    character: 'bull',
    pose: 'pointing',
    message:
      'Este é o módulo de **Alunos**. Aqui você cadastra e gerencia todos os alunos da academia — matrículas, contatos e histórico. Clique em **Novo Aluno** para cadastrar.',
    showNext: true,
    showClose: true,
    position: 'near-sidebar',
  },
  'show-plans': {
    character: 'tiger',
    pose: 'thumbs-up',
    message:
      'No módulo de **Planos** você cria planos de mensalidade. Define preços, modalidades incluídas e periodicidade. Use o botão **Novo Plano** para começar.',
    showNext: true,
    showClose: true,
    position: 'near-sidebar',
  },
  'show-calendar': {
    character: 'bull',
    pose: 'thumbs-up',
    message:
      'O **Calendário** é onde você organiza toda a agenda da academia — horários de aulas, eventos especiais e bloqueios. Clique em **Novo Evento** para criar.',
    showNext: true,
    showClose: true,
    isLast: true,
    position: 'near-sidebar',
  },
}

const POSITION_CLASSES: Record<CompanionPosition, string> = {
  'bottom-right': 'fixed right-6 bottom-6',
  'near-sidebar': 'fixed left-72 bottom-6',
  center: 'fixed inset-0 flex items-center justify-center',
  'near-content': 'fixed right-8 top-1/3',
}

function renderMessage(text: string) {
  const parts = text.split(/(\*\*.*?\*\*)/g)

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className='font-semibold'>
          {part.slice(2, -2)}
        </strong>
      )
    }

    return part
  })
}

export function TourCompanion() {
  const { isActive, currentStep, nextStep, closeTour, setUseFinancial } = useTourStore()

  if (!isActive || currentStep === 'completed') return null

  const config = STEP_CONFIG[currentStep]

  if (!config) return null

  const image = CHARACTER_IMAGES[config.character][config.pose]
  const name = CHARACTER_NAMES[config.character]
  const isCenter = config.position === 'center'

  const handleFinancialChoice = (choice: boolean) => {
    setUseFinancial(choice)
    nextStep()
  }

  const cardContent = (
    <div className={cn('z-50 flex max-w-md items-end gap-3', !isCenter && POSITION_CLASSES[config.position])}>
      <div className='relative rounded-xl border border-border bg-card p-5 shadow-lg'>
        {config.showClose && (
          <button
            onClick={closeTour}
            className='absolute top-2 right-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
            aria-label='Fechar tour'
          >
            <X className='h-4 w-4' />
          </button>
        )}

        <p className='mb-1 text-sm font-semibold text-primary'>{name}</p>
        <p className={cn('text-base text-foreground', config.showClose && 'pr-6')}>
          {renderMessage(config.message)}
        </p>

        <div className='mt-3 flex items-center gap-2'>
          {config.showFinancialChoice && (
            <>
              <Button variant='outline' onClick={() => handleFinancialChoice(false)}>
                Não
              </Button>
              <Button onClick={() => handleFinancialChoice(true)}>
                Sim
              </Button>
            </>
          )}

          {config.showNext && (
            <Button onClick={nextStep}>
              {config.isLast ? 'Concluir' : 'Próximo'}
            </Button>
          )}
        </div>
      </div>

      <img
        src={image}
        alt={name}
        className='h-36 w-36 shrink-0 object-contain pointer-events-none'
        draggable={false}
      />
    </div>
  )

  if (isCenter) {
    return (
      <div className={cn(POSITION_CLASSES.center, 'z-50')}>
        <div className='absolute inset-0 bg-black/20' onClick={closeTour} />
        <div className='relative flex max-w-md items-end gap-3'>
          <div className='relative rounded-xl border border-border bg-card p-5 shadow-lg'>
            {config.showClose && (
              <button
                onClick={closeTour}
                className='absolute top-2 right-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                aria-label='Fechar tour'
              >
                <X className='h-4 w-4' />
              </button>
            )}

            <p className='mb-1 text-sm font-semibold text-primary'>{name}</p>
            <p className={cn('text-base text-foreground', config.showClose && 'pr-6')}>
              {renderMessage(config.message)}
            </p>

            <div className='mt-3 flex items-center gap-2'>
              {config.showFinancialChoice && (
                <>
                  <Button variant='outline' onClick={() => handleFinancialChoice(false)}>
                    Não
                  </Button>
                  <Button onClick={() => handleFinancialChoice(true)}>
                    Sim
                  </Button>
                </>
              )}

              {config.showNext && (
                <Button onClick={nextStep}>
                  {config.isLast ? 'Concluir' : 'Próximo'}
                </Button>
              )}
            </div>
          </div>

          <img
            src={image}
            alt={name}
            className='h-36 w-36 shrink-0 object-contain pointer-events-none'
            draggable={false}
          />
        </div>
      </div>
    )
  }

  return cardContent
}
